import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  OnDestroy,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ImpersonationService } from 'src/app/shared/services/impersonation.service';
import { PaHttpClient } from 'src/app/clients/http-client';

type Step = 'confirm' | 'otp' | 'action' | 'success';

const OTP_COUNTDOWN = 60;

@Component({
  selector: 'pa-account-deletion-dialog',
  imports: [CommonModule, DialogModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PaHttpClient],
  templateUrl:'./delete-popover.component.html',
  styles: [`
    :host { display: contents; }

    /* Dialog shell sizing — cannot be done in Tailwind due to ::ng-deep */
    ::ng-deep .pra-deletion-dialog {
      width: 420px !important;
      border-radius: 12px !important;
      .p-dialog-header { padding: 20px 24px 12px; border-bottom: none; }
      .p-dialog-content { padding: 0; }
      .p-dialog-footer { padding: 12px 24px 24px; border-top: none; }
    }

    ::ng-deep .pra-confirm-action-dialog {
      width: 340px !important;
      border-radius: 12px !important;
      z-index: 1120 !important;
      .p-dialog-header { padding: 20px 24px 12px; border-bottom: none; }
      .p-dialog-content { padding: 0 24px; }
      .p-dialog-footer { padding: 12px 24px 24px; border-top: none; }
    }

    @media (max-width: 768px) {
      ::ng-deep .pra-deletion-dialog,
      ::ng-deep .pra-confirm-action-dialog {
        width: 90vw !important;
      }
    }

    ::ng-deep .pra-deletion-dialog--bg {
      z-index: 1100 !important;
      opacity: 1;
      pointer-events: none;
      filter: brightness(0.6);
    }

    ::ng-deep .pra-success-dialog .p-dialog-header {
      display: none;
    }

    .otp-box {
      width: 40px;
      height: 59px;
    }

   
  `]
})
export class AccountDeletionDialogComponent implements OnDestroy {
  private readonly auth   = inject(AuthService);
  private readonly impersonation = inject(ImpersonationService);
  private readonly http   = inject(PaHttpClient);
  private readonly router = inject(Router);

  // Super admin viewing-as this account when they deleted it — on close, exit
  // back to the admin session instead of logging out to the public get-started page.
  private wasImpersonating = false;

  closed = output<void>();

  // ── Signals ───────────────────────────────────────────────────────────────
  step            = signal<Step>('confirm');
  loading         = signal(false);
  otpValue        = signal('');
  otpDisplayValue = computed(() => this.otpValue().split('').join(' '));
  otpError        = signal(false);
  resendCountdown = signal(OTP_COUNTDOWN);
  confirmChecked  = false;

  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  // ── Computed ──────────────────────────────────────────────────────────────
  user          = computed(() => this.auth.getUser());
  isOtpComplete = computed(() => this.otpValue().length === 4);

  isPictureBlocked(status: string | null | undefined): boolean {
    return status === 'flagged' || status === 'blocked';
  }

  private get userId(): string {
    return this.user()?.user_id ?? '';
  }

  // ── Step 1: Send OTP ──────────────────────────────────────────────────────
  onConfirmDeletion(): void {
    this.loading.set(true);
    this.http.post('send_otp', { otp_type: 'email', user_id: this.userId }).subscribe({
      next: ({ success }: any) => {
        this.loading.set(false);
        if (!success) return;
        this.step.set('otp');
        this.startCountdown();
      },
      error: () => this.loading.set(false)
    });
  }

  // ── Step 2: Verify OTP ────────────────────────────────────────────────────
  onVerifyOtp(): void {
    const otp = this.otpValue();
    this.loading.set(true);
    this.http.post('verify_otp', { otp_type: 'email', otp, user_id: this.userId }).subscribe({
      next: ({ success }: any) => {
        this.loading.set(false);
        if (!success) { this.otpError.set(true); return; }
        this.otpError.set(false);
        this.stopCountdown();
        this.step.set('action');
      },
      error: () => {
        this.loading.set(false);
        this.otpError.set(true);
      }
    });
  }

  onResendOtp(): void {
    this.otpValue.set('');
    this.otpError.set(false);
    this.onConfirmDeletion();
  }

  // ── Step 3: Delete Account ────────────────────────────────────────────────
  onDeleteAccount(): void {
    if (!this.confirmChecked) return;
    this.loading.set(true);
    this.wasImpersonating = this.impersonation.isImpersonating();
    this.http.delete(`user/delete/${this.userId}`).subscribe({
      next: ({ success }: any) => {
        this.loading.set(false);
        if (!success) return;
        // Impersonated session's token is only cleared once the admin exits it below
        // (see onSuccessClose) — logging out here would also wipe the admin's own token.
        if (!this.wasImpersonating) {
          this.onLogout();
        }
        this.step.set('success');
      },
      error: () => this.loading.set(false)
    });
  }
  private onLogout(): void {
    this.auth.logout();
  }
  // ── Step 4: Success ───────────────────────────────────────────────────────
  onSuccessClose(): void {
    if (this.wasImpersonating) {
      this.impersonation.stopImpersonation().then(response => {
        window.location.href = response?.status ? '/home' : '/login';
      }).catch(() => this.router.navigate(['/login']));
      return;
    }
    this.router.navigate(['/login']);
  }

  // ── OTP Input handlers ────────────────────────────────────────────────────
  onOtpInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digitsOnly = input.value.replace(/\D/g, '').slice(0, 4);
    this.otpValue.set(digitsOnly);
    this.otpError.set(false);
  }

  // ── Countdown ─────────────────────────────────────────────────────────────
  private startCountdown(): void {
    this.resendCountdown.set(OTP_COUNTDOWN);
    this.stopCountdown();
    this.countdownInterval = setInterval(() => {
      const current = this.resendCountdown();
      if (current <= 0) { this.stopCountdown(); return; }
      this.resendCountdown.set(current - 1);
    }, 1000);
  }

  private stopCountdown(): void {
    if (!this.countdownInterval) return;
    clearInterval(this.countdownInterval);
    this.countdownInterval = null;
  }

  formatCountdown(seconds: number): string {
    const m = Math.floor(seconds / 60).toString();
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s} sec`;
  }

  close(): void {
    this.step.set('confirm');
    this.otpValue.set('');
    this.otpError.set(false);
    this.confirmChecked = false;
    this.stopCountdown();
    this.closed.emit();
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }
}